<template>
  <div class="configure-vasion-settings-page" :class="{ 'step-mode': isStepMode }">
    <!-- Loading Overlay -->
    <div v-if="isLoading && (selectedTenantUsesBedrock || selectedTenantUsesTextract)" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0, 0, 0, 0.5); display: flex; align-items: center; justify-content: center; z-index: 9999">
      <div style="background: white; padding: 30px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3); text-align: center">
        <div class="spinner" style="display: inline-block; width: 50px; height: 50px; border: 5px solid #f3f3f3; border-top: 5px solid #164863; border-radius: 50%"></div>
        <div style="margin-top: 20px; font-size: 18px; color: #164863; font-weight: 600">{{ $t('configureVasionSettings.savingTrainingData') }}</div>
        <div style="margin-top: 10px; font-size: 14px; color: #666">{{ $t('configureVasionSettings.pleaseWaitWhileSaving') }}</div>
      </div>
    </div>

    <!-- Header -->
    <div v-if="!isStepMode" class="headerContent">
      <div class="createDocumentTypeHeader">
        <span class="newPageTitle">{{ $t('configureVasionSettings.configureSettings') }}</span>
      </div>
    </div>

    <!-- Configure Vasion Settings Form -->
    <div class="createDocumentTypeBody">
      <div class="form-sections">
        <!-- Section 1 - Vasion Configuration -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('configureVasionSettings.vasionConfiguration') }}</h3>
            <p class="section-description">{{ $t('configureVasionSettings.vasionConfigurationDescription') }}</p>
          </div>
          <div class="section-content">
            <div class="form-grid">
              <!-- Default Automatic Vasion Integration -->
              <div class="form-field full-width">
                <label>{{ $t('configureVasionSettings.defaultAutomaticVasionIntegration') }}</label>
                <select v-model="newDocumentType.send_to_vasion_storage" class="custom-select">
                  <option :value="true">{{ $t('configureVasionSettings.sendToVasionStorage') }}</option>
                  <option :value="false">{{ $t('configureVasionSettings.startVasionWorkflow') }}</option>
                </select>
              </div>

              <!-- Divider -->
              <div class="section-divider full-width"></div>

              <!-- Storage Folders Group -->
              <div class="form-field">
                <label>{{ $t('configureVasionSettings.vasionDefaultApprovalStorage') }}</label>
                <select
                  v-model="newDocumentType.vasion_folder_id"
                  :class="{
                    'error-border': showErrors && errors.vasion_folder_id,
                  }"
                  :disabled="loadingStates.vasionFolders"
                >
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : $t('configureVasionSettings.selectApprovedFolder') }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_folder_id">
                  {{ errors.vasion_folder_id }}
                </span>
              </div>

              <div class="form-field">
                <label>{{ $t('configureVasionSettings.vasionRejectionFolder') }}</label>
                <select
                  v-model="newDocumentType.vasion_rejection_folder_id"
                  :class="{
                    'error-border': showErrors && errors.vasion_rejection_folder_id,
                  }"
                  :disabled="loadingStates.vasionFolders"
                >
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : $t('configureVasionSettings.selectRejectionFolder') }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_rejection_folder_id">
                  {{ errors.vasion_rejection_folder_id }}
                </span>
              </div>

              <div class="form-field">
                <label>{{ $t('configureVasionSettings.vasionApprovedPreviouslyRejectedFolder') }}</label>
                <select v-model="newDocumentType.vasion_approved_previously_rejected_folder_id" :disabled="loadingStates.vasionFolders">
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : $t('configureVasionSettings.selectApprovedPreviouslyRejectedFolder') }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
              </div>

              <div class="form-field">
                <label>{{ $t('configureVasionSettings.vasionReviewFolder') }}</label>
                <select
                  v-model="newDocumentType.vasion_review_folder_id"
                  :class="{
                    'error-border': showErrors && errors.vasion_review_folder_id,
                  }"
                  :disabled="loadingStates.vasionFolders"
                >
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : $t('configureVasionSettings.selectApprovalFolder') }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_review_folder_id">
                  {{ errors.vasion_review_folder_id }}
                </span>
              </div>

              <div class="form-field">
                <label>{{ $t('configureVasionSettings.vasionFailedDocumentFolder') }}</label>
                <select
                  v-model="newDocumentType.vasion_failed_folder_id"
                  :class="{
                    'error-border': showErrors && errors.vasion_failed_folder_id,
                  }"
                  :disabled="loadingStates.vasionFolders"
                >
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : $t('configureVasionSettings.selectFailedDocumentsFolder') }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="folder.FolderId">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_failed_folder_id">
                  {{ errors.vasion_failed_folder_id }}
                </span>
              </div>

              <!-- Divider -->
              <div class="section-divider full-width"></div>

              <!-- Workflow -->
              <div class="form-field full-width">
                <label>{{ $t('configureVasionSettings.vasionDefaultWorkflowName') }}</label>
                <select
                  v-model="newDocumentType.vasion_workflow_id"
                  :class="{
                    'error-border': showErrors && errors.vasion_workflow_id,
                  }"
                  :disabled="loadingStates.vasionWorkflows"
                >
                  <option value="">{{ loadingStates.vasionWorkflows ? $t('configureVasionSettings.loadingWorkflows') : $t('configureVasionSettings.selectWorkflow') }}</option>
                  <option v-for="workflow in workflows" :value="workflow.iID" :key="workflow.iID">
                    {{ workflow.sName }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_workflow_id">
                  {{ errors.vasion_workflow_id }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <!-- Section 2 - Vasion Watch Folder Configuration -->
        <div class="form-section">
          <div class="section-header section-header-with-toggle">
            <h3 class="section-title">Auto Import from Vasion</h3>
            <div class="toggle-switch-container">
              <label class="toggle-switch">
                <input type="checkbox" v-model="newDocumentType.vasion_auto_import_enabled" />
                <span class="slider"></span>
              </label>
              <span class="toggle-switch-label">{{ isVasionAutoImportEnabled ? $t('configureVasionSettings.enabled') : $t('configureVasionSettings.disabled') }}</span>
            </div>
          </div>
          <div class="section-content">
            <div class="form-grid">
              <div class="form-field" :class="{ 'is-disabled': !isVasionAutoImportEnabled }">
                <label>Select Vasion Watch Folder</label>
                <select v-model="newDocumentType.vasion_watch_folder_id" :disabled="loadingStates.vasionFolders || !isVasionAutoImportEnabled">
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : 'Select Vasion Watch Folder' }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="`watch-${folder.FolderId}`">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_watch_folder_id">
                  {{ errors.vasion_watch_folder_id }}
                </span>
              </div>
              <div class="form-field" :class="{ 'is-disabled': !isVasionAutoImportEnabled }">
                <label>Select Vasion Folder to Move Files</label>
                <select v-model="newDocumentType.vasion_completion_folder_id" :disabled="loadingStates.vasionFolders || !isVasionAutoImportEnabled">
                  <option value="">{{ loadingStates.vasionFolders ? $t('configureVasionSettings.loadingFolders') : 'Select Completion Folder' }}</option>
                  <option v-for="folder in storageFolders" :value="folder.FolderId" :key="`completion-${folder.FolderId}`">
                    {{ folder.Name }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_completion_folder_id">
                  {{ errors.vasion_completion_folder_id }}
                </span>
              </div>
              <div class="form-field" :class="{ 'is-disabled': !isVasionAutoImportEnabled }">
                <label>File Pickup Interval</label>
                <select v-model.number="newDocumentType.vasion_watch_interval_minutes" :disabled="!isVasionAutoImportEnabled">
                  <option :value="5">5 minutes</option>
                  <option :value="15">15 minutes</option>
                  <option :value="30">30 minutes</option>
                  <option :value="60">1 hour</option>
                  <option :value="120">2 hours</option>
                  <option :value="360">6 hours</option>
                  <option :value="720">12 hours</option>
                  <option :value="1440">1 day</option>
                </select>
                <span class="error-text" v-if="showErrors && errors.vasion_watch_interval_minutes">
                  {{ errors.vasion_watch_interval_minutes }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 3 - HITL Configuration -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('configureVasionSettings.hitlConfiguration') }}</h3>
            <p class="section-description">{{ $t('configureVasionSettings.hitlConfigurationDescription') }}</p>
          </div>
          <div class="section-content">
            <div class="form-grid">
              <div class="form-field">
                <label>{{ $t('configureVasionSettings.humanInTheLoopValidation') }}</label>
                <div class="toggle-field-box">
                  <span class="toggle-field-text">{{ $t('configureVasionSettings.humanInTheLoopValidation') }}</span>
                  <div class="toggle-switch-container">
                    <label class="toggle-switch">
                      <input type="checkbox" v-model="newDocumentType.requires_complete_manual_validation" />
                      <span class="slider"></span>
                    </label>
                    <span class="toggle-switch-label">{{ newDocumentType.requires_complete_manual_validation ? $t('configureVasionSettings.enabled') : $t('configureVasionSettings.disabled') }}</span>
                  </div>
                </div>
              </div>

              <div class="form-field">
                <label>{{ $t('configureVasionSettings.documentConfidenceLevelThreshold') }}</label>
                <select
                  v-model="newDocumentType.ml_confidence_level"
                  :class="{
                    'error-border': showErrors && errors.ml_confidence_level,
                  }"
                >
                  <option value="">{{ $t('configureVasionSettings.selectThreshold') }}</option>
                  <option v-for="threshold in thresholds" :value="threshold.value" :key="threshold.value">
                    {{ threshold.label }}
                  </option>
                </select>
                <span class="error-text" v-if="showErrors && errors.ml_confidence_level">
                  {{ errors.ml_confidence_level }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Splitting Configuration Section -->
        <div class="form-section">
          <div class="section-header">
            <h3 class="section-title">{{ $t('configureVasionSettings.splittingConfiguration') }}</h3>
            <p class="section-description">{{ $t('configureVasionSettings.splittingConfigurationDescription') }}</p>
          </div>
          <div class="section-content">
            <div class="splitting-options">
              <div v-if="selectedTenantUsesBedrock" class="bedrock-auto-splitting-section">
                <div class="bedrock-auto-splitting-header">
                  <div class="bedrock-auto-splitting-text">
                    <div class="bedrock-auto-splitting-title">{{ $t('configureVasionSettings.autoSplitting') }}</div>
                    <div class="bedrock-auto-splitting-description">{{ $t('configureVasionSettings.auto2') }}</div>
                  </div>
                  <div class="bedrock-auto-splitting-toggle">
                    <label class="bedrock-toggle-switch">
                      <input type="checkbox" checked disabled />
                      <span class="slider"></span>
                    </label>
                  </div>
                </div>
                <div class="bedrock-divider"></div>
                <div class="bedrock-post-classification-label">{{ $t('configureVasionSettings.postClassificationSplitting') }}</div>
              </div>
              <!-- Auto Option -->
              <div class="radio-option">
                <input type="radio" id="detection-auto" value="auto" v-model="newDocumentType.detection_mode" class="radio-input" />
                <label for="detection-auto" class="radio-label">{{ $t('configureVasionSettings.auto') }}</label>
              </div>

              <!-- Do not delete below commented code - required for qr splitting functionality -->
              <!-- QR Code Option -->
              <div class="radio-option">
                <input type="radio" id="detection-qr" value="qr" v-model="newDocumentType.detection_mode" class="radio-input" />
                <label for="detection-qr" class="radio-label">QR Code</label>
              </div>

              <!-- QR Code Splitting Strategy Options -->
              <div v-if="newDocumentType.detection_mode === 'qr'" class="conditional-field">
                <div class="sub-options">
                  <p class="sub-options-title">Select splitting strategy:</p>
                  <div class="radio-option">
                    <input type="radio" id="qr-strategy-static" value="static_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="qr-strategy-static" class="radio-label">Static Splitting</label>
                  </div>

                  <div class="radio-option">
                    <input type="radio" id="qr-strategy-dynamic" value="dynamic_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="qr-strategy-dynamic" class="radio-label">Dynamic Splitting With Variable Values</label>
                  </div>

                  <div class="radio-option">
                    <input type="radio" id="qr-strategy-separator" value="break_on_code_as_separator" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="qr-strategy-separator" class="radio-label">Splitter Page (Splitter page is only a marker and won't appear in final files)</label>
                  </div>
                  <span class="error-text" v-if="showErrors && errors.splitting_strategy && newDocumentType.detection_mode === 'qr'">
                    {{ errors.splitting_strategy }}
                  </span>
                </div>
              </div>

              <!-- Barcode Option -->
              <div class="radio-option">
                <input type="radio" id="detection-barcode" value="barcode" v-model="newDocumentType.detection_mode" class="radio-input" />
                <label for="detection-barcode" class="radio-label">Barcode</label>
              </div>

              <!-- Barcode Splitting Strategy Options -->
              <div v-if="newDocumentType.detection_mode === 'barcode'" class="conditional-field">
                <div class="sub-options">
                  <p class="sub-options-title">Select splitting strategy:</p>
                  <div class="radio-option">
                    <input type="radio" id="barcode-strategy-static" value="static_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="barcode-strategy-static" class="radio-label">Static Splitting</label>
                  </div>

                  <div class="radio-option">
                    <input type="radio" id="barcode-strategy-dynamic" value="dynamic_code_splitting" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="barcode-strategy-dynamic" class="radio-label">Dynamic Splitting With Variable Values</label>
                  </div>

                  <div class="radio-option">
                    <input type="radio" id="barcode-strategy-separator" value="break_on_code_as_separator" v-model="newDocumentType.splitting_strategy" class="radio-input" name="splitting-strategy" />
                    <label for="barcode-strategy-separator" class="radio-label">Splitter Page (Splitter page is only a marker and won't appear in final files)</label>
                  </div>
                  <span class="error-text" v-if="showErrors && errors.splitting_strategy && newDocumentType.detection_mode === 'barcode'">
                    {{ errors.splitting_strategy }}
                  </span>
                </div>
              </div>

              <!-- Page Count Option -->
              <div class="radio-option">
                <input type="radio" id="detection-page-count" value="page_count" v-model="newDocumentType.detection_mode" class="radio-input" />
                <label for="detection-page-count" class="radio-label">{{ $t('configureVasionSettings.pageCount') }}</label>
              </div>

              <!-- Page Count Input -->
              <div v-if="newDocumentType.detection_mode === 'page_count'" class="conditional-field">
                <div class="form-field">
                  <label>{{ $t('configureVasionSettings.enterPageCount') }}</label>
                  <input
                    type="number"
                    v-model.number="newDocumentType.page_number"
                    :class="{
                      'error-border': showErrors && errors.page_number,
                    }"
                    :placeholder="$t('configureVasionSettings.enterPageCountPlaceholder')"
                    min="1"
                  />
                  <span class="error-text" v-if="showErrors && errors.page_number">
                    {{ errors.page_number }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="button-group">
        <button class="finish-btn" @click="finishConfiguration" :disabled="isLoading">
          {{ getButtonText() }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { post, get, put } from '@/utils/api'
import { showToast } from '@/utils/toast'
export default {
  name: 'ConfigureVasionSettings',
  props: {
    isStepMode: {
      type: Boolean,
      default: false,
    },
    fileS3Url: {
      type: Object,
      default: () => ({}),
    },
    tenantId: {
      type: [String, Number],
      default: null,
    },
    selectedTenantUsesBedrock: {
      type: Boolean,
      default: false,
    },
    selectedTenantUsesTextract: {
      type: Boolean,
      default: false,
    },
    blueprintSchema: {
      type: Object,
      default: () => ({}),
    },
    collectedStepData: {
      type: Object,
      default: () => ({
        docPreviewData: {},
        extractionMappingList: [],
        vasionFields: {},
      }),
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    existingVasionSettings: {
      type: Object,
      default: () => ({}),
    },
    selectedFileName: {
      type: String,
      default: '',
    },
    blueprintName: {
      type: String,
      default: '',
    },
    documentTypeName: {
      type: String,
      default: '',
    },
    textractAnalysisType: {
      type: String,
      default: '',
    },
    uploadedFilePagesCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['complete', 'state-change'],
  data() {
    return {
      fileName: '',
      localDocumentTypeName: this.documentTypeName,
      classifiedAs: '',
      showErrors: false,
      isLoading: false,
      loadingStates: {
        vasionWorkflows: false,
        vasionFolders: false,
        vasionForms: false,
        activeModels: false,
      },
      newDocumentType: {
        tenantId: '',
        category_id: '',
        department_id: '',
        document_type_name: '',
        document_type_display_name: '',
        vasion_form_id: '',
        vasion_workflow_id: '',
        vasion_folder_id: '',
        vasion_rejection_folder_id: '',
        vasion_approved_previously_rejected_folder_id: '',
        vasion_review_folder_id: '',
        vasion_failed_folder_id: '',
        vasion_watch_folder_id: '',
        vasion_completion_folder_id: '',
        vasion_auto_import_enabled: false,
        vasion_watch_interval_minutes: 15,
        send_to_vasion_storage: true,
        model_id: '',
        ml_confidence_level: '',
        is_active: true,
        requires_complete_manual_validation: false,
        page_count: '',
        detection_mode: 'auto', // API key: auto, qr, barcode, page_count
        splitting_strategy: '', // API key: static_code_splitting, dynamic_code_splitting, break_on_code_as_separator
        page_number: '', // Used when detection_mode is 'page_count'
        uploaded_file_pages_count: '',
      },
      errors: {},
      // Data arrays for API-fetched dropdowns - all start empty and get populated by APIs
      vasionForms: [],
      workflows: [],
      storageFolders: [],
      activeModels: [],
      // Standard threshold options (can remain static)
      thresholds: [
        { label: '50%', value: 50 },
        { label: '60%', value: 60 },
        { label: '70%', value: 70 },
        { label: '80%', value: 80 },
        { label: '90%', value: 90 },
        { label: '100%', value: 100 },
      ],
      // Flag to track component readiness to avoid unnecessary state emissions
      isComponentReady: false,
    }
  },
  computed: {
    isVasionAutoImportEnabled() {
      return this.newDocumentType.vasion_auto_import_enabled === true || this.newDocumentType.vasion_auto_import_enabled === 'true'
    },
    // Only compute split_on_code based on your logic
    split_on_code() {
      // If page count is selected and page_number > 0, return false
      if (this.newDocumentType.detection_mode === 'page_count' && this.newDocumentType.page_number > 0) {
        return false
      }
      // Return true if QR code or Barcode is selected
      return this.newDocumentType.detection_mode === 'qr' || this.newDocumentType.detection_mode === 'barcode'
    },
  },
  async mounted() {
    // Get tenantId from props or route query
    const tenantId = this.tenantId || this.$route.query.tenant_id
    // Initialize vasion_form_id from collectedStepData if available
    if (this.collectedStepData?.vasionFormId) {
      this.newDocumentType.vasion_form_id = this.collectedStepData.vasionFormId
    }

    if (tenantId) {
      await this.fetchVasionFolders(tenantId)
      await this.fetchVasionWorkflows(tenantId)
    }

    // If in edit mode, verify we have a valid document type ID
    if (this.isEditMode) {
      const routeDocTypeId = this.$route.query.document_type_id ? Number(this.$route.query.document_type_id) : null
      // CRITICAL FIX: Use 'current_document_type_id' to match what Uploads.vue stores
      const currentDocTypeId = sessionStorage.getItem('current_document_type_id') ? Number(sessionStorage.getItem('current_document_type_id')) : null
      const fallbackDocTypeId = sessionStorage.getItem('document_type_id') ? Number(sessionStorage.getItem('document_type_id')) : null
      const docTypeId = routeDocTypeId || currentDocTypeId || fallbackDocTypeId

      if (!docTypeId || docTypeId === 0 || isNaN(docTypeId)) {
        // Alert user and potentially redirect
        alert('Error: Cannot proceed with update - no document type ID found. Please start the edit process from the Document Types list.')
      }
    }
  },
  created() {
    // Load data from props first, then route query params and sessionStorage as fallback
    this.fileName = this.selectedFileName || this.$route.query.fileName || sessionStorage.getItem('selectedFileName') || ''
    this.localDocumentTypeName = this.documentTypeName || this.$route.query.documentTypeName || sessionStorage.getItem('documentTypeName') || ''
    this.classifiedAs = this.localDocumentTypeName

    // Initialize form with the classified type
    this.newDocumentType.document_type_name = this.classifiedAs
    this.newDocumentType.document_type_display_name = this.classifiedAs
  },
  watch: {
    // Watch for changes in documentTypeName prop from parent
    documentTypeName: {
      handler(newName) {
        if (newName && newName !== this.localDocumentTypeName) {
          this.localDocumentTypeName = newName
          this.classifiedAs = newName
          this.newDocumentType.document_type_name = newName
          this.newDocumentType.document_type_display_name = newName
        }
      },
      immediate: true,
    },
    // Watch for changes in newDocumentType and emit to parent for persistence
    newDocumentType: {
      handler(newValue) {
        // Only emit if component is ready and we have meaningful data to avoid initial load emissions
        if (this.isComponentReady && (newValue.vasion_workflow_id || newValue.vasion_folder_id || newValue.ml_confidence_level || newValue.vasion_auto_import_enabled || newValue.vasion_watch_folder_id || newValue.vasion_completion_folder_id || newValue.vasion_watch_interval_minutes)) {
          // Emit state change to parent for persistence
          this.$emit('state-change', {
            vasionSettingsData: { ...newValue },
          })
        }
      },
      deep: true,
    },
    'newDocumentType.vasion_auto_import_enabled': {
      handler(newValue) {
        if (typeof newValue === 'string') {
          this.newDocumentType.vasion_auto_import_enabled = newValue === 'true'
        }
      },
    },
    existingVasionSettings: {
      handler(newSettings) {
        if (newSettings && Object.keys(newSettings).length > 0) {
          // Apply each setting to newDocumentType, but avoid triggering state-change events during initialization
          this.isComponentReady = true

          Object.keys(newSettings).forEach((key) => {
            if (newSettings[key] !== null && newSettings[key] !== undefined) {
              this.newDocumentType[key] = newSettings[key]
            }
          })

          if (newSettings.vasion_auto_import_enabled !== undefined && newSettings.vasion_auto_import_enabled !== null) {
            this.newDocumentType.vasion_auto_import_enabled = newSettings.vasion_auto_import_enabled === true || newSettings.vasion_auto_import_enabled === 'true'
          }

          // CRITICAL FIX: If vasion_workflow_id exists, it means "Start Vasion Workflow" was selected
          // So we need to set send_to_vasion_storage to false
          if (newSettings.vasion_workflow_id && newSettings.vasion_workflow_id !== '' && newSettings.vasion_workflow_id !== 0) {
            this.newDocumentType.send_to_vasion_storage = false
          }
          // If send_to_vasion_storage is not explicitly set but we have no workflow ID, default to true
          else if (newSettings.send_to_vasion_storage === undefined || newSettings.send_to_vasion_storage === null) {
            this.newDocumentType.send_to_vasion_storage = true
          }

          if (newSettings.vasion_watch_interval_minutes !== undefined && newSettings.vasion_watch_interval_minutes !== null) {
            this.newDocumentType.vasion_watch_interval_minutes = Number(newSettings.vasion_watch_interval_minutes) || 15
          } else if (newSettings.vasion_watch_interval) {
            this.newDocumentType.vasion_watch_interval_minutes = this.parseLegacyWatchIntervalToMinutes(newSettings.vasion_watch_interval)
          }

          if (newSettings.vasion_auto_import_enabled === undefined || newSettings.vasion_auto_import_enabled === null) {
            this.newDocumentType.vasion_auto_import_enabled = Boolean(this.newDocumentType.vasion_watch_folder_id || this.newDocumentType.vasion_completion_folder_id)
          }
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    parseLegacyWatchIntervalToMinutes(intervalValue) {
      const intervalMap = {
        '5min': 5,
        '15min': 15,
        '30min': 30,
        '1hr': 60,
        '2hr': 120,
        '6hr': 360,
        '12hr': 720,
        '1day': 1440,
      }
      return intervalMap[intervalValue] || 15
    },
    getButtonText() {
      if (this.isLoading) {
        return this.isEditMode ? this.$t('configureVasionSettings.updating') : this.$t('configureVasionSettings.finishing')
      }
      if (this.isEditMode) {
        return this.$t('configureVasionSettings.update')
      }
      return this.isStepMode ? this.$t('configureVasionSettings.finishTraining') : this.$t('configureVasionSettings.finish')
    },
    goBack() {
      this.$router.push('/configure-vasion')
    },
    async finishConfiguration() {
      this.showErrors = true
      this.errors = {}

      // Basic validation
      // Vasion workflow is only required when Start Vasion Workflow is selected (send_to_vasion_storage = false)
      if (!this.newDocumentType.send_to_vasion_storage && !this.newDocumentType.vasion_workflow_id) {
        this.errors.vasion_workflow_id = this.$t('configureVasionSettings.vasionWorkflowRequired')
      }

      if (!this.newDocumentType.ml_confidence_level) {
        this.errors.ml_confidence_level = this.$t('configureVasionSettings.confidenceLevelThresholdRequired')
      }

      // Page count validation
      if (this.newDocumentType.detection_mode === 'page_count') {
        if (!this.newDocumentType.page_number || this.newDocumentType.page_number <= 0) {
          this.errors.page_number = this.$t('configureVasionSettings.pageNumberGreaterThanZero')
        } else if (this.uploadedFilePagesCount > 0 && this.newDocumentType.page_number > this.uploadedFilePagesCount) {
          this.errors.page_number = this.$t('configureVasionSettings.pageNumberCannotExceedTrainedDocument', {
            pageCount: this.uploadedFilePagesCount,
          })
        }
      }

      // Splitting strategy validation for QR and Barcode
      if (this.newDocumentType.detection_mode === 'qr' || this.newDocumentType.detection_mode === 'barcode') {
        if (!this.newDocumentType.splitting_strategy) {
          this.errors.splitting_strategy = this.$t('configureVasionSettings.selectSplittingStrategy')
        }
      }
      if (this.isVasionAutoImportEnabled) {
        if (!this.newDocumentType.vasion_watch_folder_id) {
          this.errors.vasion_watch_folder_id = 'Select Vasion watch folder'
        }
        if (!this.newDocumentType.vasion_completion_folder_id) {
          this.errors.vasion_completion_folder_id = 'Select completion folder'
        }
        if (!this.newDocumentType.vasion_watch_interval_minutes || Number(this.newDocumentType.vasion_watch_interval_minutes) <= 0) {
          this.errors.vasion_watch_interval_minutes = 'Select file pickup interval'
        }
      }

      // If there are errors, don't proceed
      if (Object.keys(this.errors).length > 0) {
        return
      }

      // If using Bedrock or Textract in step mode, call the save training data API
      if ((this.selectedTenantUsesBedrock || this.selectedTenantUsesTextract) && this.isStepMode) {
        const isAutoImportEnabled = this.isVasionAutoImportEnabled
        const vasionWatchIntervalMinutes = Number(this.newDocumentType.vasion_watch_interval_minutes) || 15
        this.newDocumentType.vasion_watch_interval_minutes = vasionWatchIntervalMinutes

        // Collect Vasion fields from current form
        // Map frontend field names to backend expected field names
        const vasionFields = {
          send_to_vasion_storage: this.newDocumentType.send_to_vasion_storage,
          vasion_form_id: Number(this.newDocumentType.vasion_form_id) || 0,
          // Send null for vasion_workflow_id when Vasion Storage is selected
          vasion_workflow_id: this.newDocumentType.send_to_vasion_storage ? null : Number(this.newDocumentType.vasion_workflow_id) || 0,
          vasion_folder_id: Number(this.newDocumentType.vasion_folder_id) || 0,
          vasion_rejection_folder_id: Number(this.newDocumentType.vasion_rejection_folder_id) || 0,
          vasion_approved_previously_rejected_folder_id: Number(this.newDocumentType.vasion_approved_previously_rejected_folder_id) || 0,
          vasion_review_folder_id: Number(this.newDocumentType.vasion_review_folder_id) || 0,
          vasion_failed_folder_id: Number(this.newDocumentType.vasion_failed_folder_id) || 0,
          vasion_auto_import_enabled: isAutoImportEnabled,
          vasion_watch_folder_id: this.newDocumentType.vasion_watch_folder_id ? Number(this.newDocumentType.vasion_watch_folder_id) || 0 : 0,
          vasion_completion_folder_id: this.newDocumentType.vasion_completion_folder_id ? Number(this.newDocumentType.vasion_completion_folder_id) || 0 : 0,
          vasion_watch_interval_minutes: vasionWatchIntervalMinutes ? vasionWatchIntervalMinutes : 0,
          ml_confidence_level: Number(this.newDocumentType.ml_confidence_level) || 0,
          requires_complete_manual_validation: Boolean(this.newDocumentType.requires_complete_manual_validation),
          // Splitting configuration - map to backend field names
          splitting_type: this.newDocumentType.detection_mode || 'auto',
          qr_splitting_type: this.newDocumentType.detection_mode === 'qr' ? this.newDocumentType.splitting_strategy || '' : '',
          barcode_splitting_type: this.newDocumentType.detection_mode === 'barcode' ? this.newDocumentType.splitting_strategy || '' : '',
          page_number: this.newDocumentType.detection_mode === 'page_count' ? Number(this.newDocumentType.page_number) || 0 : 0,
        }

        // Prepare the data for API
        // Ensure we have valid collectedStepData with safety checks
        const safeCollectedStepData = {
          docPreviewData: this.collectedStepData?.docPreviewData || {},
          extractionMappingList: this.collectedStepData?.extractionMappingList || [],
        }

        // Use Map for uniqueness and prioritize user changes
        // Create a Map to ensure uniqueness by vasion_field_key
        const uniqueFieldsMap = new Map()

        // Process all fields and keep the best version of each
        safeCollectedStepData.extractionMappingList.forEach((field, index) => {
          const key = field.vasion_field_key || `unnamed_${field.id}_${index}`

          if (uniqueFieldsMap.has(key)) {
            const existingField = uniqueFieldsMap.get(key)

            // PRIORITY SYSTEM: Choose the field with more user interaction data
            let fieldToKeep = existingField

            // Priority 1: Field with boolean is_active (user interaction)
            if (typeof field.is_active === 'boolean' && typeof existingField.is_active !== 'boolean') {
              fieldToKeep = field
            }
            // Priority 2: Field with selected_bedrock_field (user selection)
            else if (field.selected_bedrock_field && !existingField.selected_bedrock_field) {
              fieldToKeep = field
            }
            // Priority 3: Field with _original property (API data)
            else if (field._original && !existingField._original) {
              fieldToKeep = field
            }
            // Priority 4: Merge if both have valuable data
            else if ((typeof field.is_active === 'boolean' && typeof existingField.is_active === 'boolean') || (field.selected_bedrock_field && existingField.selected_bedrock_field)) {
              // Both have user data - merge them with field taking priority for user interactions
              fieldToKeep = {
                ...existingField,
                // User interaction data takes priority
                is_active: typeof field.is_active === 'boolean' ? field.is_active : existingField.is_active,
                ml_confidence_level: field.ml_confidence_level !== undefined ? field.ml_confidence_level : existingField.ml_confidence_level,
                default_value_required: field.default_value_required !== undefined ? field.default_value_required : existingField.default_value_required,
                selected_bedrock_field: field.selected_bedrock_field || existingField.selected_bedrock_field,
                selected_vasion_field_key: field.selected_vasion_field_key || existingField.selected_vasion_field_key,
              }
            }

            uniqueFieldsMap.set(key, fieldToKeep)
          } else {
            // First occurrence of this field
            uniqueFieldsMap.set(key, field)
          }
        })

        // Convert Map back to array
        const uniqueFields = Array.from(uniqueFieldsMap.values())
        safeCollectedStepData.extractionMappingList = uniqueFields

        // CRITICAL: Validate that all extraction fields have proper Vasion field keys
        let hasValidationErrors = false

        safeCollectedStepData.extractionMappingList.forEach((field) => {
          if (!field.vasion_field_key || field.vasion_field_key.trim() === '') {
            hasValidationErrors = true
          }
        })

        if (hasValidationErrors) {
          alert('Error: Some extraction fields are missing Vasion field keys. This will cause integration issues with Vasion. Please refresh the page and try again.')
          this.isLoading = false
          return
        }

        // CRITICAL FIX: Transform fields to handle selected_bedrock_field vs bedrock_field logic
        const transformedExtractionMappingList = safeCollectedStepData.extractionMappingList.map((field) => {
          // CRITICAL: Detect intentional deselection vs no change
          // Priority: selected_bedrock_field (Bedrock) OR selected_vasion_field_key (Textract) > bedrock_field (stored value)
          let bedrockFieldValue = field.bedrock_field

          // Check if user has made a selection via selected_bedrock_field (Bedrock) or selected_vasion_field_key (Textract)
          if (field.hasOwnProperty('selected_bedrock_field')) {
            // BEDROCK: User has interacted with this field dropdown
            if (field.selected_bedrock_field === null || field.selected_bedrock_field === '') {
              // User selected "Select field" option (deselection) - send empty string to clear
              bedrockFieldValue = ''
            } else if (field.selected_bedrock_field) {
              // User selected a specific value
              bedrockFieldValue = field.selected_bedrock_field
            } else {
              // Fallback to existing bedrock_field
              bedrockFieldValue = field.bedrock_field || ''
            }
          } else if (field.hasOwnProperty('selected_vasion_field_key')) {
            // TEXTRACT: User has interacted with this field dropdown via selected_vasion_field_key
            if (field.selected_vasion_field_key === null || field.selected_vasion_field_key === '') {
              // User selected "Select field" option (deselection) - send empty string to clear
              bedrockFieldValue = ''
            } else if (field.selected_vasion_field_key) {
              // User selected a specific Textract value
              bedrockFieldValue = field.selected_vasion_field_key
            } else {
              // Fallback to existing bedrock_field
              bedrockFieldValue = field.bedrock_field || ''
            }
          } else if (field.ai_method_field !== undefined && field.ai_method_field !== '') {
            // Fallback to ai_method_field if available
            bedrockFieldValue = field.ai_method_field
          } else {
            // User has never interacted with this field - preserve existing DB value
            bedrockFieldValue = null
          }

          // CRITICAL FIX: Ensure is_active is properly converted to boolean
          const isActiveValue = field.is_active !== undefined ? Boolean(field.is_active) : true

          const transformed = {
            ...field,
            // Send the correct value based on user interaction
            bedrock_field: bedrockFieldValue,
            ai_method_field: bedrockFieldValue,
            // CRITICAL FIX: Ensure is_active is a proper boolean value for API
            is_active: isActiveValue,
          }

          return transformed
        })

        // Update the safeCollectedStepData with transformed fields
        safeCollectedStepData.extractionMappingList = transformedExtractionMappingList

        // SIMPLE FIX: For Textract, ensure bedrock_field and ai_method_field are set from selected_vasion_field_key
        safeCollectedStepData.extractionMappingList = safeCollectedStepData.extractionMappingList.map((field) => {
          if (field.selected_vasion_field_key && (!field.bedrock_field || field.bedrock_field === '')) {
            return {
              ...field,
              bedrock_field: field.selected_vasion_field_key,
              ai_method_field: field.selected_vasion_field_key,
            }
          }
          return field
        })

        // Ensure blueprintSchema and finalResult are properly serialized as JSON strings
        const docPreviewData = {
          blueprintArn: safeCollectedStepData.docPreviewData.blueprintArn || '',
          blueprintName: this.blueprintName || '',
          documentTypeName: this.documentTypeName || this.localDocumentTypeName || '',
          invocationArn: safeCollectedStepData.docPreviewData.invocationArn || '',
          blueprintSchema: safeCollectedStepData.docPreviewData?.blueprintSchema || '{}',
          finalResult: safeCollectedStepData.docPreviewData?.finalResult || '{}',
          s3_uri: this.fileS3Url.s3Url || '',
          s3_url: this.fileS3Url.httpsUrl || '',
        }

        // Check if we have a draft document type ID to pass for publishing
        const routeDocTypeId = this.$route.query.document_type_id ? Number(this.$route.query.document_type_id) : null
        const sessionCurrentDocTypeId = sessionStorage.getItem('current_document_type_id') ? Number(sessionStorage.getItem('current_document_type_id')) : null
        const sessionFallbackDocTypeId = sessionStorage.getItem('document_type_id') ? Number(sessionStorage.getItem('document_type_id')) : null
        // Priority: route -> session current -> session fallback
        const draftDocumentTypeId = routeDocTypeId || sessionCurrentDocTypeId || sessionFallbackDocTypeId
        const finalData = {
          docPreviewData: docPreviewData,
          ExtractionMappingList: safeCollectedStepData.extractionMappingList,
          vasionFields: vasionFields,
          tenantId: this.tenantId,
          userId: JSON.parse(localStorage.getItem('UserData'))?.id || 1,
          source_file_name: this.fileName || '',
          uploaded_file_pages_count: this.uploadedFilePagesCount,
          // Include draft document type ID if available (for publishing existing drafts)
          ...(draftDocumentTypeId && draftDocumentTypeId > 0 && { draftDocumentTypeId: draftDocumentTypeId }),
        }

        // Set loading state
        this.isLoading = true

        // Call the appropriate API based on mode
        try {
          // Determine endpoint based on AI method (Bedrock or Textract)
          let endpoint
          if (this.selectedTenantUsesTextract) {
            endpoint = this.isEditMode ? '/v1/textract/update-training-data' : '/v1/textract/save-training-data'
          } else {
            endpoint = this.isEditMode ? '/v1/bedrock/update-training-data' : '/v1/bedrock/save-training-data'
          }

          // Add document type ID and isUpdate flag if in edit mode
          if (this.isEditMode) {
            const routeDocTypeId = this.$route.query.document_type_id ? Number(this.$route.query.document_type_id) : null
            // CRITICAL FIX: Use 'current_document_type_id' to match what Uploads.vue stores
            const currentDocTypeId = sessionStorage.getItem('current_document_type_id') ? Number(sessionStorage.getItem('current_document_type_id')) : null
            const fallbackDocTypeId = sessionStorage.getItem('document_type_id') ? Number(sessionStorage.getItem('document_type_id')) : null
            const docTypeId = routeDocTypeId || currentDocTypeId || fallbackDocTypeId
            if (!docTypeId || docTypeId === 0 || isNaN(docTypeId)) {
              alert('Cannot update: No document type ID found. Please ensure you are editing an existing document type.')
              this.isLoading = false
              return
            }

            finalData.documentTypeId = docTypeId
            finalData.isUpdate = true
          }

          const response = this.isEditMode ? await put(endpoint, finalData) : await post(endpoint, finalData)

          // Treat both 200 (complete success) and 206 (partial success) as successful operations
          if (response?.data?.code === 200 || response?.data?.code === 206) {
            // Log success message regardless of whether it was complete or partial
            const successMessage = response?.data?.code === 206 ? 'Training data updated successfully with some items skipped due to constraints' : 'Training data saved successfully'
            showToast({
              title: this.$t('configureVasionSettings.successMessages.document_saved_successfully_title'),
              message: this.$t('configureVasionSettings.successMessages.document_saved_successfully_message'),
              type: 'success',
            })
            // If we just created it, store the document type ID
            // Backend returns tenantDocumentTypeId, not documentTypeId
            if (!this.isEditMode && response.data.data?.tenantDocumentTypeId) {
              sessionStorage.setItem('document_type_id', response.data.data.tenantDocumentTypeId)
            }
            // Always emit with document type ID
            const routeDocTypeId = this.$route.query.document_type_id ? Number(this.$route.query.document_type_id) : null
            // CRITICAL FIX: Use 'current_document_type_id' to match what Uploads.vue stores
            const currentDocTypeId = sessionStorage.getItem('current_document_type_id') ? Number(sessionStorage.getItem('current_document_type_id')) : null
            const fallbackDocTypeId = sessionStorage.getItem('document_type_id') ? Number(sessionStorage.getItem('document_type_id')) : null
            const docTypeId = this.isEditMode ? routeDocTypeId || currentDocTypeId || fallbackDocTypeId : response.data.data?.tenantDocumentTypeId || currentDocTypeId || fallbackDocTypeId || null
            this.$emit('complete', { documentTypeId: docTypeId })
          } else {
            throw new Error(response?.data?.message || 'Failed to save training data')
          }
        } catch (error) {
          showToast({
            title: this.$t('configureVasionSettings.failureMessages.document_saved_failure_title'),
            message: this.$t('configureVasionSettings.failureMessages.document_saved_failure_message'),
            type: 'error',
          })
        } finally {
          this.isLoading = false
        }

        return
      }

      this.isLoading = true

      // Original flow for non-Bedrock mode
      setTimeout(() => {
        this.isLoading = false

        if (this.isStepMode) {
          // In step mode, emit complete event to parent
          this.$emit('complete')
        } else {
          // In regular mode, show success message and navigate
          alert('Configuration completed successfully!')
          this.$router.push('/')
        }
      }, 1000) // Reduced timeout for better UX in step mode
    },
    async fetchVasionWorkflows(tenantId) {
      this.loadingStates.vasionWorkflows = true
      try {
        const response = await get(`/v1/vasion/workflows?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          if (response.data.data && response.data.data.Values) {
            this.workflows = response.data.data.Values
          } else {
            this.workflows = []
          }
        }
      } catch (err) {
        this.workflows = []
      } finally {
        this.loadingStates.vasionWorkflows = false
      }
    },
    async fetchVasionFolders(tenantId) {
      this.loadingStates.vasionFolders = true
      try {
        const response = await get(`/v1/vasion/folders?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          if (response.data.data && response.data.data.FolderList && response.data.data.FolderList.Values) {
            this.storageFolders = response.data.data.FolderList.Values
          } else if (response.data.data && Array.isArray(response.data.data)) {
            this.storageFolders = response.data.data
          } else {
            this.storageFolders = []
          }
        }
      } catch (err) {
        this.storageFolders = []
      } finally {
        this.loadingStates.vasionFolders = false
      }
    },
    async fetchActiveModels(tenantId) {
      this.loadingStates.activeModels = true
      try {
        const response = await get(`/v1/classification-models/active?tenantId=${tenantId}`)
        if (response?.data?.code === 200) {
          if (response.data.data && Array.isArray(response.data.data)) {
            this.activeModels = response.data.data
          } else {
            this.activeModels = []
          }
        }
      } catch (err) {
        this.activeModels = []
      } finally {
        this.loadingStates.activeModels = false
      }
    },

    // Method to get current state for persistence
    getCurrentState() {
      return {
        vasionSettingsData: { ...this.newDocumentType },
      }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');

/* Spinner animation */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

.configure-vasion-settings-page {
  padding: 20px;
  background-color: #f8f9fa;
  height: 100vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

/* Step Mode Adjustments */
.configure-vasion-settings-page.step-mode {
  margin-left: 0;
  padding: 0;
  background-color: var(--secondary-color);
  height: auto;
  min-height: calc(100vh - 200px);
}

/* Regular Mode - Account for sidebar */
.configure-vasion-settings-page:not(.step-mode) {
  margin-left: 155px;
}

.headerContent {
  margin-bottom: 20px;
}

.createDocumentTypeHeader {
  display: flex;
  align-items: center;
  gap: 10px;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 9px;
}

.newPageTitle {
  color: var(--primary-color);
  font-family: Archivo, sans-serif;
  font-size: 20px;
  font-weight: 500;
  line-height: 150%;
}

.createDocumentTypeBody {
  background: #fff;
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 25px;
  flex: 1;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px 25px;
}

.form-field {
  width: 100%;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.section-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 16px 0;
  grid-column: 1 / -1;
}

.form-field label {
  color: #8b8888;
  font-family: Archivo;
  font-size: 18px;
  font-weight: 400;
  display: block;
  margin-bottom: 6px;
}

.form-field input,
.form-field select {
  width: 100%;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.form-field select:disabled,
.form-field input:disabled {
  background-color: #f1f3f5;
  color: #9aa3ab;
  border-color: #e3e7eb;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 1;
}

.form-field.is-disabled label {
  color: #b0b7bf;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-section {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
}

.section-header {
  padding: 16px 24px;
  background: #f8f9fa;
  border-bottom: 1px solid #e8e8e8;
}

.section-header-with-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  color: var(--primary-color);
  font-family: Archivo;
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  margin-bottom: 4px;
}

.section-description {
  color: #5c656c;
  font-family: Archivo;
  font-size: 14px;
  margin: 0;
}

.section-content {
  padding: 8px;
}

.toggle-field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 47px;
  padding: 0 12px;
}

.toggle-label {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
}

.error-border {
  border-color: #dc3545 !important;
}

.error-text {
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
  display: block;
  font-family: Archivo;
}

/* Splitting Configuration Styles */
.splitting-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.radio-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.radio-label {
  font-family: Archivo;
  font-size: 16px;
  font-weight: 400;
  color: #32393e;
  cursor: pointer;
  margin: 0;
}

.conditional-field {
  margin-left: 24px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
}

.sub-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sub-options-title {
  font-family: Archivo;
  font-size: 14px;
  font-weight: 600;
  color: #32393e;
  margin: 0 0 8px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #e8e8e8;
}

.button-group {
  display: flex;
  justify-content: center;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.finish-btn {
  background: var(--primary-color);
  color: #fff;
  border: 2px solid var(--primary-color);
  padding: 12px 32px;
  border-radius: 6px;
  font-family: Archivo;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.finish-btn:hover {
  background: #1565c0;
  border-color: #1565c0;
}

.finish-btn:disabled {
  background: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
}

/* Toggle Switch Styles */
.toggle-field-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 47px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #dbdbdb;
  background: #fff;
}

.toggle-field-text {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  flex: 1;
}

.toggle-switch-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  vertical-align: middle;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  background-color: #ccc;
  border-radius: 24px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 0.3s;
}

.toggle-switch input:checked + .slider {
  background-color: var(--primary-color, #3b82f6);
}

.slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(26px);
}

.toggle-switch-label {
  font-family: Archivo;
  font-size: 16px;
  color: #32393e;
  font-weight: 500;
}

/* Bedrock Auto-Splitting Specific Styles */
.bedrock-auto-splitting-section {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.bedrock-auto-splitting-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.bedrock-auto-splitting-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.bedrock-auto-splitting-title {
  font-family: Archivo;
  font-size: 16px;
  font-weight: 500;
  color: #32393e;
  margin: 0;
}

.bedrock-auto-splitting-description {
  font-family: Archivo;
  font-size: 14px;
  font-weight: 400;
  color: #5c656c;
  margin: 0;
}

.bedrock-auto-splitting-toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.bedrock-toggle-switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  vertical-align: middle;
  cursor: not-allowed;
}

.bedrock-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.bedrock-toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  background-color: var(--primary-color, #3b82f6);
  border-radius: 24px;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: 0.3s;
}

.bedrock-toggle-switch input:checked + .slider {
  background-color: var(--primary-color, #3b82f6);
}

.bedrock-toggle-switch .slider:before {
  position: absolute;
  content: '';
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  border-radius: 50%;
  transition: 0.3s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.bedrock-toggle-switch input:checked + .slider:before {
  transform: translateX(26px);
}

.bedrock-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 16px 0 0 0;
  width: 100%;
}

.bedrock-post-classification-label {
  font-family: Archivo;
  margin: 16px 0 0 0;
  padding-top: 16px;
  font-size: 16px;
  font-weight: 500;
  color: #32393e;
  margin: 0;
}
</style>
